import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import { AppModel, type DoitRole, type RoleModel, UserModel, type UserPermission } from "@doitintl/cmp-models";
import { getCollection, type ModelReference, type WithModel } from "@doitintl/models-any";
import { type User } from "firebase/auth";
import merge from "lodash/merge";
import { type SetNonNullable, type SetRequired } from "type-fest";

import Permissions from "../utils/permissions";
import { useAuthContext } from "./AuthContext";
import { useModelsStore } from "./ModelsContext";

// firebase supports user without email, but our system doesn't allow login require email provider (google, ms, user/pass)
// using the native firebase.User will require a lot of places to check for the email but we already know globally that the user has email
// extend the type of current user to reflect that
export type FirebaseUserWithEmail = Omit<User, "email"> & {
  email: string;
};

export type UserModelType = Omit<WithModel<UserModel>, "roles"> & {
  id: string;
  ref: ModelReference<UserModel>;
  roles: ({ id: string } & WithModel<RoleModel>)[];
};

type UserContextType = {
  userModel?: UserModelType;
};

type FullUserContextType = UserContextType & {
  userRoles?: Permissions | null;
};

type FullUserContextTypeWithUser = Required<FullUserContextType> & {
  customerId?: string;
};

const userContext = createContext<FullUserContextType | undefined>(undefined);
const userContextGuard = createContext<boolean>(false);

type TUseUserContextParams<
  TAllowNull extends boolean | undefined,
  TRequiredRoles extends boolean | undefined,
> = TRequiredRoles extends undefined
  ? TAllowNull extends true | undefined
    ? FullUserContextType
    : Omit<FullUserContextTypeWithUser, "userRoles">
  : TRequiredRoles extends true
    ? TAllowNull extends undefined
      ? SetNonNullable<SetRequired<FullUserContextTypeWithUser, "userRoles">, "userRoles">
      : FullUserContextTypeWithUser
    : never;

export function useUserContext<
  TAllowNull extends boolean | undefined = undefined,
  TRequiredRoles extends boolean | undefined = undefined,
>({ allowNull, requiredRoles }: { allowNull?: TAllowNull; requiredRoles?: TRequiredRoles } = {}): TUseUserContextParams<
  TAllowNull,
  TRequiredRoles
> {
  const context = useContext(userContext);
  const guardContext = useContext(userContextGuard);

  // if requiredRoles is true, we need to make sure that userRoles is not null also
  const actualAllowNull = allowNull ?? !requiredRoles;

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (guardContext && actualAllowNull) {
        // eslint-disable-next-line no-console
        console.warn(
          `useUserContext was used with requiredRoles: ${requiredRoles} | allowNull: ${allowNull} but userRoles is set. This is not a good practice and should be avoided.`
        );
        // eslint-disable-next-line no-console
        console.trace(); // separately call trace to print the stack
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  }

  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }

  if (requiredRoles && !guardContext) {
    throw new Error("useUserContext was used with requiredRoles: true, outside UserRolesGuard");
  }

  if (!actualAllowNull && !context.userModel) {
    throw new Error("useUserContext was used without user");
  }

  if (requiredRoles && !context.userRoles) {
    throw new Error("useUserContext requiredRoles: true was used without userRoles");
  }

  return context as TUseUserContextParams<TAllowNull, TRequiredRoles>;
}

const useRoles = ({ userModel, doitRoles }: { userModel?: UserModelType; doitRoles?: DoitRole[] }) => {
  const { isDoitEmployee, isDoitPartner, isDoitDeveloper, isDoitOwner, userId } = useAuthContext();
  const [userRoles, setUserRoles] = useState<Permissions | null>();

  useEffect(() => {
    // if its a doit employee wait for the doitRoles to be set
    if (!userModel?.ref || (isDoitEmployee && !doitRoles)) {
      setUserRoles(null);
      return;
    }

    setUserRoles(
      new Permissions(
        {
          permissions: userModel.permissions,
          roles: userModel.roles,
        },
        { isDoitEmployee, isDoitPartner, isDoitDeveloper, isDoitOwner, userId },
        doitRoles
      )
    );
  }, [
    userModel?.ref,
    userModel?.permissions,
    userModel?.roles,
    isDoitEmployee,
    doitRoles,
    isDoitPartner,
    isDoitDeveloper,
    isDoitOwner,
    userId,
  ]);

  return [userRoles];
};

export const UserContextProvider = ({ value, children }: { value: UserContextType; children?: ReactNode }) => {
  const { currentUser, isDoitEmployee } = useAuthContext();
  const { modelsStoreReady } = useModelsStore();
  const [doitRoles, setDoitRoles] = useState<DoitRole[]>();

  useEffect(() => {
    // hack to set the id of the currentUSer as many places in the code assume it's there
    if (value.userModel && currentUser) {
      (currentUser as any).id = value.userModel.id;
    }
  }, [currentUser, value.userModel]);

  useEffect(() => {
    if (!currentUser?.email || !isDoitEmployee || !modelsStoreReady) {
      return;
    }

    return getCollection(AppModel)
      .doc("doit-employees")
      .collection("doitRoles")
      .where("users", "array-contains", currentUser.email)
      .onSnapshot((querySnapshot) => {
        setDoitRoles(() => querySnapshot.docs.map((docSnap) => docSnap.get("roleName")));
      });
  }, [currentUser?.email, isDoitEmployee, modelsStoreReady]);

  const [userRoles] = useRoles({ userModel: value.userModel, doitRoles });

  return (
    <userContext.Provider
      value={{
        ...value,
        userRoles,
      }}
    >
      {children}
    </userContext.Provider>
  );
};

export type TestingRole = { name: string; permissions: { id: string }[] };

export const UserContextProviderForTesting = ({
  children,
  value,
}: {
  children?: ReactNode;
  value?: Partial<
    Omit<UserModelType, "userModel"> & {
      doitRoles?: DoitRole[];
      userModel: Partial<
        Omit<UserModelType, "roles"> & {
          permissions?: UserPermission[];
          roles?: TestingRole[];
        }
      >;
    }
  >;
}) => {
  const { token, customerId, userId, currentUser, isDoitEmployee, isDoitPartner, isDoitDeveloper, isDoitOwner } =
    useAuthContext();

  if (!token) {
    return null;
  }

  const defaultUserModel: Partial<typeof value> = {
    displayName: currentUser?.displayName ?? "",
    email: currentUser?.email,
    id: userId,
    roles: [],
    customer: customerId ? ({ ref: { id: customerId } } as any) : undefined,
  };

  const mergedUserModel = merge({}, defaultUserModel, value?.userModel ?? {});
  const doitRoles = value?.doitRoles || (isDoitEmployee ? [] : undefined);
  mergedUserModel.ref = getCollection(UserModel).doc(mergedUserModel.id as string);

  const userRoles = new Permissions(
    mergedUserModel,
    {
      isDoitEmployee,
      isDoitPartner,
      isDoitDeveloper,
      isDoitOwner,
      userId,
    },
    doitRoles
  );

  const providerValue = { userModel: mergedUserModel as any, userRoles } as any;

  return (
    <userContext.Provider value={providerValue}>
      <userContextGuard.Provider value={true}>{children}</userContextGuard.Provider>
    </userContext.Provider>
  );
};

export const UserRolesGuard = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuthContext();
  const { userRoles } = useUserContext();

  if (!userRoles || !currentUser) {
    return null;
  }

  return <userContextGuard.Provider value={true}>{children}</userContextGuard.Provider>;
};
